import { useEffect } from 'react';
import { useTraits, useTraitsActions } from '@atlassian/jira-traits/src/TraitsStore.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { Trait } from '@atlassian/jira-traits/src/types.tsx';
import { normalizeTopCollaboratorAccountIds } from './normalizeTopCollaboratorAccountIds.tsx';

export const TOP_ROVO_CHAT_COLLABORATORS_TRAIT = 'rovo_chat_top_collaborators_per_site';
export const IS_ROVO_CHAT_MAU_TRAIT = 'is_rovo_chat_mau';

export type FomoTraitsResource = {
	/** Comma-separated string of up to 3 collaborator AAIDs. Empty string when missing/loaded-empty. */
	collaboratorAaids: string;
	/**
	 * `true` if the user is a Rovo Chat MAU (and therefore disqualified from the nudge).
	 * `false` if the trait is `'false'` OR missing entirely (graceful default).
	 * `null` while the ATLASSIAN_ACCOUNT trait is still loading.
	 */
	isRovoChatMau: boolean | null;
	/** True once both collaborator and MAU trait scopes have been resolved. */
	loaded: boolean;
};

export const getTopRovoChatCollaboratorsFromTrait = (
	traitValue: Trait['value'] | undefined,
): string => {
	if (typeof traitValue !== 'string' || traitValue.trim() === '') {
		return '';
	}

	return normalizeTopCollaboratorAccountIds(traitValue.split(',')).join(',');
};

const getTraitValue = (attributes: Trait[] | null, traitName: string): Trait['value'] | undefined =>
	attributes?.find((trait) => trait.name === traitName)?.value;

/**
 * Returns both FOMO-relevant TAP traits in a single resource:
 *   - `collaboratorAaids`: top 3 collaborator AAIDs as a comma-separated string
 *   - `isRovoChatMau`: whether the user is already a Rovo Chat MAU (disqualifier)
 *   - `loaded`: whether both trait scopes have resolved
 *
 * The collaborator trait lives in the SITE_USER scope because it is
 * site-specific. The MAU disqualifier stays in ATLASSIAN_ACCOUNT scope.
 *
 * Missing `is_rovo_chat_mau` attribute is treated as `false` (graceful default —
 * don't accidentally block the nudge if TAP hasn't delivered the trait yet).
 */
export const useTopRovoChatCollaboratorsTraitResource = (
	traitName: string = TOP_ROVO_CHAT_COLLABORATORS_TRAIT,
	enabled: boolean = true,
): FomoTraitsResource => {
	const accountId = useAccountId();
	const cloudId = useCloudId();
	const { fetchTraits } = useTraitsActions();
	const [traitsState] = useTraits();
	const accountTraits = traitsState.ATLASSIAN_ACCOUNT;
	const siteUserTraits = traitsState.SITE_USER;

	useEffect(() => {
		if (!enabled) {
			return;
		}
		fetchTraits(['SITE_USER', 'ATLASSIAN_ACCOUNT'], { cloudId, accountId });
	}, [fetchTraits, cloudId, accountId, enabled]);

	if (!siteUserTraits.loaded || !accountTraits.loaded) {
		return { collaboratorAaids: '', isRovoChatMau: null, loaded: false };
	}

	const collaboratorAaids = getTopRovoChatCollaboratorsFromTrait(
		getTraitValue(siteUserTraits.attributes, traitName),
	);

	// Missing trait → treat as `false` (not MAU → allow nudge).
	const rawMauValue = getTraitValue(accountTraits.attributes, IS_ROVO_CHAT_MAU_TRAIT);
	const isRovoChatMau = rawMauValue != null && String(rawMauValue).toLowerCase() === 'true';

	return { collaboratorAaids, isRovoChatMau, loaded: true };
};
